// apps/provider/src/services/api/reporting.js
// Practice Reporting — dashboard, operations, revenue cycle, trends, and export.
//
// Methods: getReportingDashboard, getReportingOperations, getReportingVisitAlerts, getReportingBillingOps,
//          getReportingRevenueCycle, getReportingExport, getProviderSummary
//
// The heavy endpoints sit behind the reporting admission gate (Xano
// functions/reporting_admission, 2026-08-29) and answer 429 when the instance
// is saturated; those getters retry through retryOnBusy.
import { retryOnBusy } from '../../utils/retryOnBusy';

/**
 * @returns {Object} Reporting domain methods (bound to `this` of the composed apiService)
 */

export function createReportingMethods() {
  return {

    /**
     * Get the calendar Patient Report: one row per active-case patient with
     * their most recent past appointment (days since seen, status, location)
     * and earliest upcoming appointment.
     *
     * Xano: GET /calendar-patient-report
     * Xano staging file: calendar-patient-report_GET.xs
     *
     * Organization is derived from auth token server-side ($auth.extras.organization_id).
     * Threshold/location filtering and sorting are done client-side.
     *
     * @returns {Promise<import('@prism-ehr/shared').CalendarPatientReportRow[]>}
     */
    getCalendarPatientReport() {
      // One row per ACTIVE CASE since 2026-09-21 (case-level last/next dates,
      // patient_next_appointment_* hint); same column names as the old
      // per-patient shape plus case_id / case_name / provider_id.
      return this.request('/calendar-patient-report', {
        method: 'GET'
      });
    },

    /**
     * Get aggregated reporting dashboard metrics for an organization.
     *
     * Xano: GET /reporting/dashboard
     * Xano staging file: reporting/dashboard_GET.xs
     *
     * Organization is derived from auth token server-side ($auth.extras.organization_id).
     * Returns patient, productivity, and payment metrics for the given period.
     * Top-level `productivity_basis` echoes organization.settings.productivity_basis
     * (billed_minutes | scheduled_duration | visit_capacity). Under visit_capacity,
     * productivity.productivity_pct = visits / productivity.potential_visits and
     * by_provider rows carry potential_visits (0 under the other bases).
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.start_date] — YYYY-MM-DD
     * @param {string} [filters.end_date] — YYYY-MM-DD
     * @param {string} [filters.provider_id] — optional single provider filter
     * @param {string} [filters.location_id] — optional location filter
     * @returns {Promise<{patients: Object, productivity: Object, payment: Object}>}
     */
    /**
     * Visit-type breakdown (by appointment type / note template / pending
     * documentation / documentation-only encounters) for the Performance tab
     * Visits drill-down. Fetched on demand when the drill-down opens.
     * GET /reporting/visit-types
     * @returns {Promise<{appt_type: Array, note_template: Array, pending_doc: Array, non_visit: Array}>}
     */
    getReportingVisitTypes(filters = {}, options = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      // provider_id: exemption hint only (an excluded user named here keeps their rows); never a row filter
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.nocache) queryParams.append('nocache', '1');
      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/visit-types${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 90000,
        signal: options.signal
      }), { signal: options.signal });
    },

    getReportingDashboard(filters = {}, options = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.compare_provider_ids) queryParams.append('compare_provider_ids', filters.compare_provider_ids);
      if (filters.compare_location_ids) queryParams.append('compare_location_ids', filters.compare_location_ids);
      if (filters.nocache) queryParams.append('nocache', '1');

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/dashboard${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 90000,
        signal: options.signal
      }), { signal: options.signal });
    },

    /**
     * Get lightweight provider summary metrics for home page badges.
     *
     * Xano: GET /reporting/provider-summary
     *
     * Returns only: points, productivity_pct, treatment_minutes, paid_minutes, visits,
     * potential_visits (visit_capacity basis only, else 0).
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.start_date] — YYYY-MM-DD
     * @param {string} [filters.end_date] — YYYY-MM-DD
     * @param {string} [filters.provider_id] — optional, defaults to auth user server-side
     * @returns {Promise<{points: number, productivity_pct: number, treatment_minutes: number, paid_minutes: number, visits: number}>}
     */
    /**
     * Get per-provider export data for configurable CSV download.
     *
     * Xano: GET /reporting/export
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.start_date] — YYYY-MM-DD
     * @param {string} [filters.end_date] — YYYY-MM-DD
     * @param {string} [filters.location_id] — optional location filter
     * @param {string} [filters.export_type] — defaults to 'provider_productivity'
     * @param {string} [filters.aggregation] — 'provider' (default) or 'location'
     * @returns {Promise<{providers: Array, meta: Object}>}
     */
    getReportingExport(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.location_ids) queryParams.append('location_ids', filters.location_ids);
      else if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.export_type) queryParams.append('export_type', filters.export_type);
      if (filters.aggregation) queryParams.append('aggregation', filters.aggregation);

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/export${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 180000,
      }), {});
    },

    /**
     * Get operations metrics for the reporting Operations tab.
     *
     * Xano: GET /reporting/operations
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.start_date] — YYYY-MM-DD
     * @param {string} [filters.end_date] — YYYY-MM-DD
     * @param {string} [filters.provider_id]
     * @param {string} [filters.location_id]
     * @returns {Promise<{scheduling: Object, documentation: Object, authorizations: Object, referrals: Object}>}
     */
    getReportingOperations(filters = {}, options = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.location_ids) queryParams.append('location_ids', filters.location_ids);
      else if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.nocache) queryParams.append('nocache', '1');

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/operations${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 90000,
        signal: options.signal
      }), { signal: options.signal });
    },

    /**
     * Get visit alerts for the reporting Operations tab "Visit Alerts" section:
     * prescribed-visit exhaustion, benefit-limit exhaustion, under-scheduled
     * patients. Born dark (Tier 1); the tab gates it behind
     * VISIT_ALERTS_REPORT_ENABLED until the load check passes.
     *
     * Xano: GET /reporting/visit-alerts
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.start_date] — YYYY-MM-DD
     * @param {string} [filters.end_date] — YYYY-MM-DD
     * @param {string} [filters.provider_id] - one case therapist id, or a CSV of ids (Coverage > Visit Limits)
     * @param {string} [filters.location_id]
     * @returns {Promise<{prescribed: {items: Array, total_count: number}, benefit: {items: Array, total_count: number}, under_scheduled: {items: Array, total_count: number}, thresholds: Object}>}
     */
    getReportingVisitAlerts(filters = {}, options = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.nocache) queryParams.append('nocache', '1');

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/visit-alerts${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 90000,
        signal: options.signal
      }), { signal: options.signal });
    },

    /**
     * Online self-service actions (patient cancels / reschedules / waitlist
     * joins from the reminder link or the portal) for the Operations tab.
     * Tier 3: two org-bounded indexed reads on appointment_self_service_log.
     *
     * Xano: GET /reporting/self-service-actions
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.start_date] — YYYY-MM-DD (default: 30 days ago)
     * @param {string} [filters.end_date] — YYYY-MM-DD (default: today)
     * @param {string} [filters.provider_id]
     * @param {string} [filters.location_id]
     * @param {number} [filters.page]
     * @param {number} [filters.per_page] — max 100
     * @returns {Promise<{window: {start_date: string, end_date: string}, summary: Object, summary_capped: boolean, items: Array, itemsTotal: number, curPage: number, pageTotal: number}>}
     */
    getReportingSelfServiceActions(filters = {}, options = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.page) queryParams.append('page', String(filters.page));
      if (filters.per_page) queryParams.append('per_page', String(filters.per_page));

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/self-service-actions${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 30000,
        signal: options.signal
      }), { signal: options.signal });
    },

    /**
     * Get billing operations metrics (charge posting per day/poster, hold
     * created/released/open aging) for the reporting Billing Ops tab. Born dark
     * (Tier 1); Reporting.jsx gates the tab behind BILLING_OPS_TAB_ENABLED.
     *
     * Xano: GET /reporting/billing-ops
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.start_date] — YYYY-MM-DD
     * @param {string} [filters.end_date] — YYYY-MM-DD
     * @param {string} [filters.provider_id]
     * @param {string} [filters.location_id]
     * @returns {Promise<{posting: {days: Array, by_poster: Array, totals: Object}, holds: {created: Array, released: Array, open_aging: Array, top_reasons: Array}, computed_at: string}>}
     */
    getReportingBillingOps(filters = {}, options = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.nocache) queryParams.append('nocache', '1');

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/billing-ops${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 90000,
        signal: options.signal
      }), { signal: options.signal });
    },

    /**
     * Get revenue cycle analytics for the reporting Revenue Cycle tab.
     *
     * Xano: GET /reporting/revenue-cycle
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.start_date] — YYYY-MM-DD
     * @param {string} [filters.end_date] — YYYY-MM-DD
     * @param {string} [filters.provider_id]
     * @param {string} [filters.location_id]
     * @param {number} [filters.months] — trend lookback months (default 6)
     * @returns {Promise<{ar_analysis: Object, denial_analysis: Object, collection_performance: Object, payer_performance: Array, unbilled_analysis: Object}>}
     */
    getReportingRevenueCycle(filters = {}, options = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.location_ids) queryParams.append('location_ids', filters.location_ids);
      else if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.months) queryParams.append('months', filters.months);
      if (filters.nocache) queryParams.append('nocache', '1');

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/revenue-cycle${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 90000,
        signal: options.signal
      }), { signal: options.signal });
    },

    /**
     * Get monthly trend data for the Outcomes & Trends tab.
     *
     * Xano: GET /reporting/trends
     *
     * @param {Object} [filters={}]
     * @param {number} [filters.months] — number of months to return (default 6, max 12)
     * @param {string} [filters.provider_id]
     * @param {string} [filters.location_id]
     * @returns {Promise<{months: Array<{month: string, visits: number, new_patients: number, revenue: number, productivity_pct: number, potential_visits: number, points_per_week: number, visits_per_patient: number}>}>}
     */
    getReportingTrends(filters = {}, options = {}) {
      const queryParams = new URLSearchParams();
      if (filters.months) queryParams.append('months', filters.months);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.nocache) queryParams.append('nocache', '1');

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/trends${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 90000,
        signal: options.signal
      }), { signal: options.signal });
    },

    /**
     * Get per-encounter revenue export rows (month-close Revenue report).
     *
     * Xano: GET /reporting/revenue-export
     *
     * One row per completed encounter filtered by date of service. Includes
     * billed/paid amounts plus estimated amounts (contracted rate or 100% of
     * Medicare) so revenue can be projected before claims are paid.
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.start_date] — YYYY-MM-DD (date of service)
     * @param {string} [filters.end_date] — YYYY-MM-DD (date of service)
     * @param {string} [filters.location_ids] — comma-separated location filter
     * @param {string} [filters.location_id] — single location filter
     * @returns {Promise<{encounters: import('@prism-ehr/shared/types/api/billing').RevenueExportRow[], meta: Object}>}
     */
    getRevenueExport(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.location_ids) queryParams.append('location_ids', filters.location_ids);
      else if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.care_setting) queryParams.append('care_setting', filters.care_setting);

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/revenue-export${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 180000,
      }), {});
    },

    /**
     * Get remittance adjustment export rows (month-close Adjustments report).
     *
     * Xano: GET /reporting/adjustments-export
     *
     * One row per ERA detail line posted in the period (by remittance payment
     * date), showing contractual and other adjustments.
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.start_date] — YYYY-MM-DD (posted date)
     * @param {string} [filters.end_date] — YYYY-MM-DD (posted date)
     * @param {string} [filters.location_ids] — comma-separated location filter
     * @param {string} [filters.location_id] — single location filter
     * @returns {Promise<{adjustments: import('@prism-ehr/shared/types/api/billing').AdjustmentsExportRow[], meta: Object}>}
     */
    getAdjustmentsExport(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.location_ids) queryParams.append('location_ids', filters.location_ids);
      else if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.care_setting) queryParams.append('care_setting', filters.care_setting);

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/adjustments-export${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 180000,
      }), {});
    },

    /**
     * Get cash posting export rows (month-close Cash Posting report).
     *
     * Xano: GET /reporting/cash-posting-export
     *
     * One row per payment event posted in the period: insurance (ERA),
     * patient card (Stripe), and manual (cash/check/zelle/venmo) payments.
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.start_date] — YYYY-MM-DD (posted date)
     * @param {string} [filters.end_date] — YYYY-MM-DD (posted date)
     * @param {string} [filters.location_ids] — comma-separated location filter
     * @param {string} [filters.location_id] — single location filter
     * @param {string} [filters.exclude_dos_before] — YYYY-MM-DD; drops rows with a service date before this (rows with no service date are kept)
     * @param {'payment'|'posted'|'deposit'} [filters.date_basis] — payment (default): ERA payment/check date window; posted: adjudication ledger facts created in the window (when applied in Prism); deposit: the confirmed bank deposit date the remit is matched to in Bank Rec, else the remit's manual deposit_date, else the remit date (org setting cash_basis = deposit_date)
     * @returns {Promise<{payments: import('@prism-ehr/shared/types/api/billing').CashPostingExportRow[], meta: Object}>}
     */
    getCashPostingExport(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.location_ids) queryParams.append('location_ids', filters.location_ids);
      else if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.care_setting) queryParams.append('care_setting', filters.care_setting);
      if (filters.exclude_dos_before) queryParams.append('exclude_dos_before', filters.exclude_dos_before);
      if (filters.date_basis) queryParams.append('date_basis', filters.date_basis);

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/cash-posting-export${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 180000,
      }), {});
    },

    /**
     * Get AR aging export rows (month-close AR Aging report).
     *
     * Xano: GET /reporting/ar-aging-export
     *
     * One row per completed encounter (same entry point as Revenue) with
     * collection status overlay — billed, paid, outstanding, aging bucket.
     * Billed totals tie to Revenue for the same DOS range.
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.start_date] — YYYY-MM-DD
     * @param {string} [filters.end_date] — YYYY-MM-DD
     * @param {string} [filters.location_ids] — comma-separated location filter
     * @param {string} [filters.location_id] — single location filter
     * @param {string} [filters.date_mode] — 'service_date' (default) or 'last_billed'; last_billed anchors aging to the current claim's submission date
     * @returns {Promise<{encounters: import('@prism-ehr/shared/types/api/billing').ARAgingExportRow[], meta: Object}>}
     */
    getArAgingExport(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.location_ids) queryParams.append('location_ids', filters.location_ids);
      else if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.care_setting) queryParams.append('care_setting', filters.care_setting);
      if (filters.date_mode) queryParams.append('date_mode', filters.date_mode);

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/ar-aging-export${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 180000,
      }), {});
    },

    getAppointmentDetailExport(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/appointment-detail-export${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 180000,
      }), {});
    },

    getPayrollExport(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.location_id) queryParams.append('location_id', filters.location_id);

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/payroll-export${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 180000,
      }), {});
    },

    /**
     * Get 8-minute opportunity report data.
     *
     * Xano: GET /reporting/8min-opportunity
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.start_date] — YYYY-MM-DD
     * @param {string} [filters.end_date] — YYYY-MM-DD
     * @param {string} [filters.location_id]
     * @param {string} [filters.location_ids]
     * @returns {Promise<{encounters: Array, summary: Object, meta: Object}>}
     */
    getEightMinuteOpportunity(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.location_ids) queryParams.append('location_ids', filters.location_ids);
      else if (filters.location_id) queryParams.append('location_id', filters.location_id);

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/8min-opportunity${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 90000,
      }), {});
    },

    /**
     * Get P&L export data with dual cost model.
     *
     * Xano: GET /reporting/pnl-export
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.start_date] — YYYY-MM-DD
     * @param {string} [filters.end_date] — YYYY-MM-DD
     * @param {string} [filters.location_id]
     * @param {string} [filters.location_ids]
     * @param {string} [filters.aggregation] — 'provider' (default) or 'location'
     * @returns {Promise<{rows: Array, meta: Object}>}
     */
    getPnlExport(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.location_ids) queryParams.append('location_ids', filters.location_ids);
      else if (filters.location_id) queryParams.append('location_id', filters.location_id);
      if (filters.aggregation) queryParams.append('aggregation', filters.aggregation);

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/pnl-export${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 180000,
      }), {});
    },

    getProviderSummary(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);

      const query = queryParams.toString();
      return this.request(`/reporting/provider-summary${query ? `?${query}` : ''}`, {
        method: 'GET'
      });
    },

    getProviderPointDetail(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);

      const query = queryParams.toString();
      return this.request(`/reporting/provider-point-detail${query ? `?${query}` : ''}`, {
        method: 'GET'
      });
    },

    getClinicMetricsExport(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.location_ids) queryParams.append('location_ids', filters.location_ids);
      else if (filters.location_id) queryParams.append('location_id', filters.location_id);

      const query = queryParams.toString();
      return retryOnBusy(() => this.request(`/reporting/clinic-metrics-export${query ? `?${query}` : ''}`, {
        method: 'GET',
        timeoutMs: 180000,
      }), {});
    },

  };
}
